// ディクテーション（書き取り）オーバーレイ。プレーヤ内のモードとして開き、同じ <video>/<audio>
// 要素(media)を流用して区間再生する（再ロード/再デコードしない＝デスクトップが #player を流用する思想）。
// 採点は text-diff.js(LCS 単語アライメント)、文分割は sentences.js、成績は lib/dictation.js(ローカル)。
// スマホ向けに縦1カラム：上=文ナビ/進捗・リプレイ/ループ、中=入力＋採点＋色分け結果、下=折りたたみ文リスト。
import { segmentSentences } from '../lib/sentences.js'
import { tokenizeWords, normalizeWord, alignLCS } from '../lib/text-diff.js'
import { getProgress, record } from '../lib/dictation.js'
import { t } from '../lib/i18n.js'
import { ICONS } from './icons.js'

const accClass = (a) => (a >= 80 ? 'ok' : a >= 50 ? 'mid' : 'bad')

// 本文マスク（英数字を●に。句読点は残して文のリズムだけ見せる）。
function maskText(text) {
  return String(text).replace(/[A-Za-z0-9'-]+/g, (w) => '●'.repeat(Math.min(w.length, 6)))
}

// 原文を語ごとに span 化し、status[wi](true=当たり/false=外し) で色分け。辞書タップは外側(attachDict)で有効化。
function renderSentence(text, status) {
  const wrap = document.createElement('div')
  wrap.className = 'dct-sentence sub-line'   // .sub-line にするとプレーヤのタップ辞書(.word)対象になる
  const parts = String(text).split(/([A-Za-z0-9][A-Za-z0-9'-]*)/)
  let wi = 0
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i]
    if (!seg) continue
    if (i % 2 === 1) {
      const span = document.createElement('span')
      let cls = 'word'
      if (status && status[wi] === true) cls += ' dctOk'
      else if (status && status[wi] === false) cls += ' dctBad'
      span.className = cls
      span.textContent = seg
      wrap.appendChild(span)
      wi++
    } else {
      wrap.appendChild(document.createTextNode(seg))
    }
  }
  return wrap
}

// 開く。deps = { root, media, hash, mainCues, seek }。戻り値 { close, isActive }。
// seek(t) はプレーヤ本体の seek（ループ対象cueの追従などを巻き込まないよう media.currentTime 直も可）。
export function openDictation({ root, media, hash, mainCues, onClose, onWordTap }) {
  // ---- 文へまとめる ----
  const sentences = segmentSentences(mainCues)

  // ---- DOM ----
  const ov = document.createElement('div')
  ov.className = 'dct-ov'
  ov.innerHTML = `
    <div class="dct-head">
      <div class="dct-title">${t('dct.title')}</div>
      <div class="dct-overall" id="dctOverall"></div>
      <button class="dct-x" id="dctClose" title="${t('dct.exit')}">${ICONS.close}</button>
    </div>
    <div class="dct-body" id="dctBody"></div>`
  root.appendChild(ov)
  const $ = (id) => ov.querySelector('#' + id)
  const body = $('dctBody')
  const overallEl = $('dctOverall')

  let progress = {}
  let filter = 'all'
  let visible = []
  let idx = -1
  let stopAt = 0
  let armed = false
  let graded = false
  let closed = false

  // 字幕(テキスト)が無い素材は採点不能 → 空状態。
  if (!sentences.length) {
    body.innerHTML = `<div class="dct-empty">${t('dct.noSubs')}</div>`
    overallEl.textContent = ''
    $('dctClose').onclick = () => close()
    wireMediaGuards()
    return { close, isActive: () => !closed }
  }

  body.innerHTML = `
    <div class="dct-practice">
      <div class="dct-nav">
        <button class="dct-rico" id="dctPrev" title="${t('dct.prev')}">${ICONS.prevSeg}</button>
        <button class="dct-rico" id="dctPlay" title="${t('dct.playpause')}">${ICONS.play}</button>
        <button class="dct-rico" id="dctReplay" title="${t('dct.replay')}">${ICONS.replay}</button>
        <button class="dct-rico" id="dctNext" title="${t('dct.next')}">${ICONS.nextSeg}</button>
        <button class="dct-rico" id="dctLoop" title="${t('dct.loop')}">${ICONS.loopOne}</button>
        <span class="dct-progress" id="dctProgress"></span>
      </div>
      <textarea class="dct-input" id="dctInput" rows="2" enterkeyhint="done"
        autocapitalize="off" autocorrect="off" spellcheck="false"
        placeholder="${t('dct.inputPlaceholder')}"></textarea>
      <div class="dct-score" id="dctScore"></div>
      <div class="dct-result" id="dctResult" hidden></div>
      <div class="dct-actions">
        <button class="dct-btn" id="dctGrade">${ICONS.grade}<span>${t('dct.grade')}</span></button>
        <button class="dct-btn secondary" id="dctReveal">${ICONS.reveal}<span>${t('dct.reveal')}</span></button>
      </div>
    </div>
    <div class="dct-listwrap">
      <div class="dct-filters">
        <button class="dct-filter active" data-filter="all">${t('dct.filterAll')}</button>
        <button class="dct-filter" data-filter="todo">${t('dct.filterTodo')}</button>
        <button class="dct-filter" data-filter="weak">${t('dct.filterWeak')}</button>
      </div>
      <div class="dct-list" id="dctList"></div>
    </div>`

  const input = $('dctInput')
  const scoreEl = $('dctScore')
  const resultEl = $('dctResult')
  const progressEl = $('dctProgress')
  const listEl = $('dctList')
  const loopBtn = $('dctLoop')
  const playBtn = $('dctPlay')
  let loop = false

  // ===== 区間再生（自前 timeupdate。armed のときだけ終端で停止/ループ） =====
  function onTimeUpdate() {
    if (closed || !armed || media.paused) return
    if (media.currentTime >= stopAt - 0.02) {
      const s = sentences[idx]
      if (loop && s) media.currentTime = s.start + 0.001
      else { media.pause(); armed = false }
    }
  }
  media.addEventListener('timeupdate', onTimeUpdate)

  function playSegment() {
    const s = sentences[idx]
    if (!s) return
    stopAt = s.end
    armed = true
    try {
      media.currentTime = s.start + 0.001
      const r = media.play()
      if (r && r.catch) r.catch(() => {})
    } catch { /* noop */ }
  }

  // 再生/一時停止トグル。一時停止からの再生は今の文を区間として再開（端で停止/ループ）。
  // 文末まで来て止まっている場合は頭から流し直す。
  function togglePlay() {
    const s = sentences[idx]
    if (!s) return
    if (media.paused) {
      stopAt = s.end
      if (media.currentTime < s.start || media.currentTime >= s.end - 0.05) media.currentTime = s.start + 0.001
      armed = true
      try { const r = media.play(); if (r && r.catch) r.catch(() => {}) } catch { /* noop */ }
    } else {
      media.pause()
    }
  }
  // ボタンのアイコンを実際の再生状態に追従（リスナは close() で解除）。
  function syncPlayIcon() { playBtn.innerHTML = media.paused ? ICONS.play : ICONS.pause }
  media.addEventListener('play', syncPlayIcon)
  media.addEventListener('pause', syncPlayIcon)

  // ===== 成績・一覧 =====
  const attemptedCount = () => Object.values(progress).filter((e) => e && e.attempts > 0).length
  function renderOverall() {
    const total = sentences.length
    const done = attemptedCount()
    const bests = Object.values(progress).filter((e) => e && e.attempts > 0).map((e) => e.best)
    const avg = bests.length ? Math.round(bests.reduce((a, b) => a + b, 0) / bests.length) : 0
    overallEl.textContent = t('dct.overall', { done, total }) + (bests.length ? t('dct.overallAvg', { avg }) : '')
  }

  function filteredIndices() {
    const out = []
    for (let i = 0; i < sentences.length; i++) {
      const e = progress[i]
      const done = e && e.attempts > 0
      if (filter === 'todo' && done) continue
      if (filter === 'weak' && !(done && e.best < 80)) continue
      out.push(i)
    }
    return out
  }

  function renderList() {
    visible = filteredIndices()
    listEl.replaceChildren()
    if (!visible.length) {
      const m = document.createElement('div')
      m.className = 'dct-listempty'
      m.textContent = t(filter === 'todo' ? 'dct.emptyTodo' : 'dct.emptyNone')
      listEl.appendChild(m)
      return
    }
    for (const i of visible) {
      const s = sentences[i]
      const e = progress[i]
      const done = e && e.attempts > 0
      const row = document.createElement('button')
      row.className = 'dct-row' + (i === idx ? ' active' : '')
      row.dataset.idx = i
      const no = document.createElement('span')
      no.className = 'dct-no'
      no.textContent = '#' + (i + 1)
      const txt = document.createElement('span')
      txt.className = 'dct-rtext' + (done ? '' : ' masked')
      txt.textContent = done ? s.text : maskText(s.text)
      const badge = document.createElement('span')
      badge.className = 'dct-badge' + (done ? ' ' + accClass(e.best) : '')
      badge.textContent = done ? e.best + '%' : t('dct.badgeTodo')
      row.append(no, txt, badge)
      row.addEventListener('click', () => selectSentence(i))
      listEl.appendChild(row)
    }
  }

  function refreshRow(i) {
    const row = listEl.querySelector(`.dct-row[data-idx="${i}"]`)
    if (!row) return
    const e = progress[i]
    const done = e && e.attempts > 0
    const txt = row.querySelector('.dct-rtext')
    txt.className = 'dct-rtext' + (done ? '' : ' masked')
    txt.textContent = done ? sentences[i].text : maskText(sentences[i].text)
    const badge = row.querySelector('.dct-badge')
    badge.className = 'dct-badge' + (done ? ' ' + accClass(e.best) : '')
    badge.textContent = done ? e.best + '%' : t('dct.badgeTodo')
  }

  function markActiveRow() {
    listEl.querySelectorAll('.dct-row').forEach((r) => r.classList.toggle('active', Number(r.dataset.idx) === idx))
  }

  // ===== 文の選択・採点 =====
  function indexAtTime(tt) {
    let cur = 0
    for (let k = 0; k < sentences.length; k++) {
      if (sentences[k].start <= (tt || 0) + 0.001) cur = k
      else break
    }
    return cur
  }

  function prepare(i) {
    idx = i
    graded = false
    input.value = ''
    input.disabled = false
    resultEl.hidden = true
    resultEl.replaceChildren()
    scoreEl.textContent = ''
    scoreEl.className = 'dct-score'
    const e = progress[i]
    const hist = e && e.attempts > 0 ? t('dct.progressHist', { best: e.best, attempts: e.attempts }) : ''
    progressEl.textContent = t('dct.progress', { n: i + 1, total: sentences.length }) + hist
    markActiveRow()
    const row = listEl.querySelector(`.dct-row[data-idx="${i}"]`)
    if (row) row.scrollIntoView({ block: 'nearest' })
  }

  function selectSentence(i) {
    if (i < 0 || i >= sentences.length) return
    prepare(i)
    playSegment()
    input.focus()
  }

  async function grade() {
    if (!sentences[idx] || graded) return
    const ref = sentences[idx].text
    const refTok = tokenizeWords(ref)
    const refNorm = refTok.map(normalizeWord).filter(Boolean)
    const hypNorm = tokenizeWords(input.value).map(normalizeWord).filter(Boolean)
    const al = alignLCS(refNorm, hypNorm)
    const acc = refNorm.length ? Math.round((al.lcs / refNorm.length) * 100) : 100
    graded = true
    input.disabled = true
    scoreEl.textContent = t('dct.score', { acc, lcs: al.lcs, total: refNorm.length })
    scoreEl.className = 'dct-score ' + accClass(acc)
    showAnswer(ref, al.keepA, t('dct.answerLabel'))
    try {
      const entry = await record(hash, idx, acc)
      if (entry) progress[idx] = entry
    } catch { /* noop */ }
    refreshRow(idx)
    renderOverall()
  }

  function reveal() {
    if (!sentences[idx]) return
    graded = true
    input.disabled = true
    scoreEl.textContent = t('dct.revealed')
    scoreEl.className = 'dct-score'
    showAnswer(sentences[idx].text, null, t('dct.answerPlain'))
  }

  function showAnswer(text, status, label) {
    resultEl.hidden = false
    resultEl.replaceChildren()
    const lbl = document.createElement('div')
    lbl.className = 'dct-lbl'
    lbl.textContent = label
    resultEl.appendChild(lbl)
    resultEl.appendChild(renderSentence(text, status))
  }

  // 結果欄は stage 外なので本体のスクラブ辞書が拾わない。語タップを onWordTap でプレーヤ本体の辞書へ橋渡し。
  if (onWordTap) {
    resultEl.addEventListener('pointerdown', (e) => {
      const w = e.target && e.target.closest ? e.target.closest('.word') : null
      if (!w) return
      e.preventDefault()
      onWordTap(w, e.clientX, e.clientY)
    })
  }

  function step(dir) {
    if (!visible.length) return
    let pos = visible.indexOf(idx)
    if (pos < 0) pos = dir > 0 ? -1 : visible.length
    const np = pos + dir
    if (np < 0 || np >= visible.length) return
    selectSentence(visible[np])
  }

  function setFilter(f) {
    filter = f
    ov.querySelectorAll('.dct-filter').forEach((b) => b.classList.toggle('active', b.dataset.filter === f))
    renderList()
  }

  // ===== イベント =====
  playBtn.onclick = togglePlay
  syncPlayIcon()
  $('dctReplay').onclick = playSegment
  $('dctPrev').onclick = () => step(-1)
  $('dctNext').onclick = () => step(1)
  $('dctGrade').onclick = grade
  $('dctReveal').onclick = reveal
  loopBtn.onclick = () => { loop = !loop; loopBtn.classList.toggle('on', loop) }
  ov.querySelectorAll('.dct-filter').forEach((b) => b.addEventListener('click', () => setFilter(b.dataset.filter)))
  $('dctClose').onclick = () => close()
  // Enter で採点 → 次の文へ（スマホは改行と競合しないよう Enter 単独のみ）。
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (graded) step(1)
      else grade()
    }
  })

  wireMediaGuards()

  // ---- 初期化（成績読込→一覧→今の位置の文から）----
  overallEl.textContent = t('dct.loading')
  ;(async () => {
    try { progress = (await getProgress(hash)) || {} } catch { progress = {} }
    if (closed) return
    renderOverall()
    renderList()
    selectSentence(indexAtTime(media.currentTime))
  })()

  function wireMediaGuards() { /* placeholder: 将来 ON 中の自由再生連動を足す余地 */ }

  function close() {
    if (closed) return
    closed = true
    armed = false
    media.removeEventListener('timeupdate', onTimeUpdate)
    media.removeEventListener('play', syncPlayIcon)
    media.removeEventListener('pause', syncPlayIcon)
    try { media.pause() } catch { /* noop */ }
    ov.remove()
    if (onClose) onClose()
  }

  return { close, isActive: () => !closed }
}
